import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Modal, Button, Spin } from 'antd';
import { StepPanel } from './StepPanel';
import VehicleInformationField from './VehicleInformationField';
import VehicleImagesUpload from './VehicleImagesUpload';
import { fetchVehicleImages } from '../../../store/actions/vehicleImages-actions';
import VehicleAdditionalDetails from './VehicleAdditionalDetails';

const VehicleForm = ({
  open,
  onCancel,
  selectedVehicle,
  addVehicle,
  editVehicle,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this state

  const { vehicleImages } = useSelector((state) => state.vehicleImage);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setActiveStep(0);
      setFileList([]);
      if (selectedVehicle) {
        form.setFieldsValue(selectedVehicle);
      }
    }
  }, [form, selectedVehicle, open]);

  const onSubmit = async (values) => {
    setIsSubmitting(true); // Set loading state to true when submission starts

    let body = { ...values };

    // Remove any keys related to image type (keys that start with 'imageType_')
    Object.keys(body).forEach((key) => {
      if (key.startsWith('imageType_')) {
        delete body[key]; // Remove imageType keys
      }
    });

    Object.keys(body).forEach((key) => {
      if (body[key] === null) {
        delete body[key];
      }
    });

    try {
      if (selectedVehicle) {
        let editBody = {};
        Object.keys(selectedVehicle).forEach((key) => {
          if (key in values) {
            const newValue = values[key] || null;
            if (selectedVehicle[key] !== newValue) {
              editBody[key] = newValue;
            }
          }
        });

        Object.keys(editBody).forEach((key) => {
          if (editBody[key] === null) {
            delete editBody[key];
          }
        });

        if (Object.keys(editBody).length === 0) {
          return;
        }

        // Edit the vehicle, passing fileList as well
        await editVehicle(editBody, fileList);
      } else {
        // Add new vehicle, passing fileList as well
        await addVehicle(body, fileList);
      }
    } catch (error) {
      console.error('Error during form submission', error);
    } finally {
      setIsSubmitting(false); // Set loading state to false when submission finishes
    }
  };

  const props = {
    name: 'file',
    multiple: true,
    onChange({ fileList }) {
      setFileList(fileList.filter((file) => file.status !== 'error'));
    },
    beforeUpload() {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(fileList);
        reader.onload = () => {
          const canvas = document.createElement('canvas');
          const img = document.createElement('img');
          img.src = reader.result;
          img.onload = () => {
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            ctx.fillStyle = 'red';
            ctx.textBaseline = 'middle';
            ctx.fillText('Test', 20, 20);
            canvas.toBlob(resolve);
          };
        };
      });
    },
    listType: 'picture',
    accept: 'image/*',
    maxCount: 12,
  };

  const cancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  const validateStep = async () => {
    const currentStep = steps[activeStep];
    const fieldNames = currentStep.content.props.fieldNames || [];

    try {
      await form.validateFields(fieldNames);
      return true;
    } catch (errorInfo) {
      console.log('Validation failed:', errorInfo);
      return false;
    }
  };

  const nextStep = async () => {
    const isValid = await validateStep();
    if (isValid) {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const steps = [
    {
      step: 1,
      title: t('form.vehicleForm.vehicleSpecifications'),
      content: (
        <VehicleInformationField
          form={form}
          selectedVehicle={selectedVehicle}
          fieldNames={[
            'vehicleMakeId',
            'vehicleModelId',
            'vehicleGenerationId',
            'vehicleEquipments',
          ]}
        />
      ),
    },
    {
      step: 2,
      title: t('form.vehicleForm.additionalDetails'),
      content: (
        <VehicleAdditionalDetails
          form={form}
          fieldNames={[
            'color',
            'engineSize',
            'gearbox',
            'modelYear',
            'horsePower',
            'description',
            'bodyType',
            'fuelType',
          ]}
        />
      ),
    },
    {
      step: 3,
      title:  t('form.vehicleForm.mediaUpload'),
      content: (
        <VehicleImagesUpload
          props={props}
          fileList={fileList}
          setFileList={setFileList}
        />
      ),
    },
  ];

  return (
    <Modal
      open={open}
      width={1000}
      forceRender
      title={selectedVehicle ? t('form.vehicleForm.editTitle') : t('form.vehicleForm.createTitle')}
      okText={selectedVehicle ? t('form.vehicleForm.editButton') : t('form.vehicleForm.addButton')}
      cancelText={t('form.vehicleForm.cancelButton')}
      onCancel={cancel}
      footer={null}
      confirmLoading={isSubmitting}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            form.resetFields();
            onSubmit(values);
          })
          .catch((info) => {
            console.log('Validate Failed:', info);
          });
      }}
    >
      <div>
        <Form
          form={form}
          layout="vertical"
          name="form"
          initialValues={selectedVehicle}
          onFinish={onSubmit}
        >
          <StepPanel
            steps={steps}
            nextStep={nextStep}
            activeStep={activeStep}
            prevStep={prevStep}
            isSubmitting={isSubmitting}
          />
        </Form>
      </div>
    </Modal>
  );
};

export default VehicleForm;
